<template>
  <div class="container-fluid" id="community-page">

    <!-- Page Header -->
    <div class="page-header gold mt-4 mb-4">
      <back-btn to="/dashboard"></back-btn>
      <h2 class="mb-0">
        <i class="fas fa-users pr-2"></i>Community
      </h2>
    </div>

    <!-- Loading State -->
    <div v-if="loading.pending" class="text-center py-5">
      <b-spinner class="align-middle" style="color: #d6b674;"></b-spinner>
      <p class="mt-2 text-muted">Loading community...</p>
    </div>

    <div v-else>

      <!-- Section 0: Pending Connection Requests -->
      <div v-if="pendingRequests.length" id="pending-requests" class="community-section my-5">
        <h4 class="section-heading mb-3">
          <i class="fas fa-user-clock mr-2"></i>
          Connection Requests
          <b-badge pill style="background:#d6b674;color:#fff;" class="ml-2">{{ pendingRequests.length }}</b-badge>
        </h4>

        <div
          v-for="member in pendingRequests"
          :key="'req-' + member.id"
          class="request-row"
        >
          <div class="member-row-avatar-wrap"
               v-b-tooltip.hover
               :title="member.is_available ? 'Available' : 'Unavailable'"
          >
            <router-link :to="'/member/' + member.id + '?from=/community'">
              <img
                :src="member.profile_image || defaultAvatar"
                :alt="member.full_name"
                class="member-row-avatar"
                @error="onImgError"
              />
            </router-link>
            <span class="member-status-dot" :class="member.is_available ? 'dot-available' : 'dot-unavailable'"></span>
          </div>

          <div class="member-row-info">
            <router-link :to="'/member/' + member.id + '?from=/community'" class="member-row-name-link" v-b-tooltip.hover title="View Profile">
              <p class="member-row-name">{{ member.full_name }}</p>
            </router-link>
            <p v-if="memberLocation(member)" class="member-row-location text-muted">
              <i class="fas fa-map-marker-alt mr-1"></i>
              {{ memberLocation(member) }}
            </p>
          </div>

          <div class="member-row-actions">
            <b-button size="sm" class="btn-gold mr-1" @click="acceptRequest(member)">
              <i class="fas fa-check mr-1"></i> Accept
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="declineRequest(member)">
              <i class="fas fa-times mr-1"></i> Decline
            </b-button>
            <b-button size="sm" class="btn-gold" @click="openRequestModal(member)">
              <i class="fas fa-hands-helping mr-1"></i> <span class="btn-text"> Request a process</span>
            </b-button>
          </div>
        </div>
      </div>

      <!-- Section 2: Available to hold space -->
      <div class="mb-5">
        <community-available-list :hide-connect="true"></community-available-list>
      </div>

      <!-- Section 1: My Connections -->
      <div class="community-section mb-4">
        <h4 class="section-heading mb-3">
          <i class="fas fa-link mr-2"></i>My Connections
          <b-badge pill style="background:#d6b674;color:#fff;" class="ml-2">{{ connections.length }}</b-badge>
        </h4>

        <!-- Search -->
        <div class="row mb-3">
          <div class="col-md-5 col-12">
            <b-input-group size="sm">
              <b-form-input
                v-model="searchQuery"
                type="search"
                placeholder="Search by name, city, or country..."
              ></b-form-input>
              <b-input-group-append>
                <b-button @click="searchQuery = ''">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="connections.length === 0" class="text-center py-4 text-muted">
          <i class="fas fa-user-friends fa-2x mb-2"></i>
          <p>You have no connections yet.</p>
        </div>

        <div v-else-if="filteredConnections.length === 0" class="text-center py-4 text-muted">
          <i class="fas fa-search fa-2x mb-2"></i>
          <p>No connections found.</p>
        </div>

        <div v-else>
          <div class="connections-scroll">
          <div
            v-for="member in paginatedConnections"
            :key="'conn-' + member.id"
            class="member-row"
          >
            <div class="member-row-avatar-wrap"
                 v-b-tooltip.hover
                 :title="member.is_available ? 'Available' : 'Unavailable'"
            >
              <router-link :to="'/member/' + member.id + '?from=/community'">
                <img
                  :src="member.profile_image || defaultAvatar"
                  :alt="member.full_name"
                  class="member-row-avatar"
                  @error="onImgError"
                />
              </router-link>
              <span class="member-status-dot" :class="(!member.is_processing_blocked && member.is_available) ? 'dot-available' : 'dot-unavailable'"></span>
            </div>

            <div class="member-row-info">
              <router-link :to="'/member/' + member.id + '?from=/community'" class="member-row-name-link" v-b-tooltip.hover title="View Profile">
                <p class="member-row-name">{{ member.full_name }}</p>
              </router-link>
              <p class="member-row-location text-muted" style="font-size:0.72rem; margin:0;">
                {{ (!member.is_processing_blocked && member.is_available) ? 'Available' : 'Unavailable' }}
              </p>
            </div>

            <div class="member-row-actions">
              <b-button
                v-if="member.is_processing_blocked"
                size="sm"
                class="btn-processing-blocked"
                disabled
                v-b-tooltip.hover="'This person is currently unavailable for processing'"
              >
                <i class="fas fa-times-circle mr-1"></i> Unavailable
              </b-button>
              <b-button
                v-else
                size="sm"
                class="btn-gold"
                @click="openRequestModal(member)"
              >
                <i class="fas fa-hands-helping mr-1"></i> <span class="btn-text"> Request a process</span>
              </b-button>
            </div>
          </div>

          </div><!-- /connections-scroll -->

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-4" v-if="filteredConnections.length > pageSize">
            <small class="text-muted">
              Showing {{ paginationStart + 1 }}–{{ paginationEnd }} of {{ filteredConnections.length }} connections
            </small>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredConnections.length"
              :per-page="pageSize"
              align="right"
              size="sm"
              class="mb-0"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>

    <!-- Processing Request Modal -->
    <b-modal
      id="processing-request-modal"
      v-model="showRequestModal"
      hide-footer
      centered
      :title="requestSent ? 'Request Sent' : 'Request to Process'"
    >
      <!-- Confirmation state -->
      <div v-if="requestSent" class="text-center py-3">
        <i class="fas fa-check-circle fa-3x mb-3" style="color:#28a745;"></i>
        <h5>Request sent!</h5>
        <p class="text-muted">
          Your request has been sent to <strong>{{ requestTarget && requestTarget.full_name }}</strong>.<br />
          They'll be notified by email and in the app.
        </p>
        <b-button class="btn-gold mt-2" @click="goToChat">
          <i class="fas fa-comment-dots mr-1"></i> Open Chat
        </b-button>
        <b-button variant="link" class="mt-2 d-block mx-auto text-muted" @click="showRequestModal = false">
          Close
        </b-button>
      </div>

      <!-- Request form -->
      <div v-else>
        <p class="text-muted mb-3">
          Send a request to <strong>{{ requestTarget && requestTarget.full_name }}</strong> to hold space for a processing session.
        </p>
        <b-form-textarea
          v-model="requestMessage"
          rows="4"
          class="mb-3"
          style="border-color:#d6b674; resize:none;"
        ></b-form-textarea>
        <div class="d-flex justify-content-end" style="gap:10px;">
          <b-button variant="link" class="text-muted" @click="showRequestModal = false">
             Cancel
          </b-button>
          <b-button
            class="btn-gold"
            :disabled="sendingRequest"
            @click="sendProcessingRequest"
          >
            <i v-if="sendingRequest" class="fas fa-spinner fa-spin mr-1"></i>
            <i v-else class="fas fa-paper-plane mr-1"></i>
            Send Request
          </b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import axios from '@/axios';
import BackBtn from "@/components/BackBtn.vue";
import CommunityAvailableList from "@/components/Processing/CommunityAvailableList.vue";

const DEFAULT_AVATAR = 'https://media.iamconnected.com/profile_images/default.png';

export default {
  name: 'Community',
  components: {CommunityAvailableList, BackBtn},

  data() {
    return {
      loading: {
        connections: false,
        pending: false,
      },
      connections: [],
      pendingRequests: [],
      searchQuery: '',
      currentPage: 1,
      pageSize: 20,
      defaultAvatar: DEFAULT_AVATAR,
      showRequestModal: false,
      requestTarget: null,
      requestMessage: '',
      sendingRequest: false,
      requestSent: false,
    };
  },

  computed: {
    filteredConnections() {
      const q = this.searchQuery.toLowerCase().trim();
      if (!q) return this.connections;
      return this.connections.filter(m =>
        (m.full_name && m.full_name.toLowerCase().includes(q)) ||
        (m.city && m.city.toLowerCase().includes(q)) ||
        (m.country && m.country.toLowerCase().includes(q))
      );
    },

    paginationStart() {
      return (this.currentPage - 1) * this.pageSize;
    },

    paginationEnd() {
      return Math.min(this.paginationStart + this.pageSize, this.filteredConnections.length);
    },

    paginatedConnections() {
      return this.filteredConnections.slice(this.paginationStart, this.paginationEnd);
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },

  mounted() {
    this.fetchPendingRequests()

    this.fetchConnections().then(() => {
      if (this.$route.query.requests && this.pendingRequests.length) {
        this.$nextTick(() => {
          const el = document.getElementById('pending-requests');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    });
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchConnections();
    });
  },

  methods: {
    memberLocation(member) {
      const parts = [member.city, member.country].filter(Boolean);
      return parts.join(', ');
    },

    onImgError(e) {
      e.target.src = DEFAULT_AVATAR;
    },

    async fetchConnections() {
      this.loading.connections = true;
      try {
        const response = await axios({
          url: 'connections/connected/list?with-availability=1',
        });

        this.connections = response.data || [];
      } catch (err) {
        console.error('Community fetch error:', err);
      } finally {
        this.loading.connections = false;
      }
    },

    async fetchPendingRequests() {
      this.loading.pending = true;
      try {
        const response = await axios({
          url: 'connections/pending',
        });

        this.pendingRequests = response.data || [];
      } catch (err) {
        console.error('Community fetch error:', err);
      } finally {
        this.loading.pending = false;
      }
    },

    async acceptRequest(member) {
      this.pendingRequests = this.pendingRequests.filter(m => m.id !== member.id);
      this.connections.push({ ...member, connection_status: 'connected' });
      try {
        await axios({
          url: 'connectionAccept',
          params: { sender_id: member.id },
        });
      } catch (err) {
        console.error('Accept error:', err);
        this.connections = this.connections.filter(m => m.id !== member.id);
        this.pendingRequests.push({ ...member, connection_status: 'pending_incoming' });
      }
    },

    openRequestModal(member) {
      this.requestTarget = member;
      this.requestMessage = "Hey, I'm currently contracted and would really appreciate your support to hold space for a process. Are you available now or sometime soon?";
      this.requestSent = false;
      this.sendingRequest = false;
      this.showRequestModal = true;
    },

    async sendProcessingRequest() {
      if (!this.requestMessage.trim() || !this.requestTarget) return;
      this.sendingRequest = true;
      try {
        const response = await axios({
          method: 'post',
          url: 'processingRequest',
          data: {
            target_user_id: this.requestTarget.id,
            message: this.requestMessage.trim(),
            contraction_id: this.$route.query.contraction_id || null,
          },
        });
        if (response.data && response.data.status === 1) {
          this.requestSent = true;
        }
      } catch (err) {
        console.error('Processing request error:', err);
      } finally {
        this.sendingRequest = false;
      }
    },

    goToChat() {
      this.showRequestModal = false;
      if (this.requestTarget) {
        this.$router.push({ path: '/chatbox/' + this.requestTarget.id, query: { from: '/community' } });
      }
    },

    async declineRequest(member) {
      this.pendingRequests = this.pendingRequests.filter(m => m.id !== member.id);
      try {
        await axios({
          url: 'connectionDecline',
          params: { sender_id: member.id },
        });
      } catch (err) {
        console.error('Decline error:', err);
        this.pendingRequests.push({ ...member, connection_status: 'pending_incoming' });
      }
    },
  },
};
</script>

<style scoped>
/* ------------------------------------------------------------------ */
/* Page shell                                                           */
/* ------------------------------------------------------------------ */
#community-page {
  padding: 0;
  max-width: 1100px;
  margin: 0 auto;
}

.gold {
  color: #d6b674;
}

.member-count-badge {
  background-color: #d6b674;
  color: #fff;
  font-size: 0.8rem;
  vertical-align: middle;
}

/* ------------------------------------------------------------------ */
/* Section headings                                                     */
/* ------------------------------------------------------------------ */
.section-heading {
  border-left: 4px solid #d6b674;
  padding-left: 12px;
  color: #333;
  font-weight: 600;
}

/* ------------------------------------------------------------------ */
/* Availability dot                                                     */
/* ------------------------------------------------------------------ */
.availability-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  vertical-align: middle;
}

.dot-green {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
}

/* ------------------------------------------------------------------ */
/* Member status dot                                                    */
/* ------------------------------------------------------------------ */
.member-status-dot {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  cursor: default;
}

.member-status-dot.dot-available {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
}

.member-status-dot.dot-unavailable {
  background-color: #ccc;
}

/* ------------------------------------------------------------------ */
/* Name hover link                                                      */
/* ------------------------------------------------------------------ */
.member-row-name-link {
  text-decoration: none;
  color: inherit;
}

.member-row-name-link:hover .member-row-name {
  text-decoration: underline;
  color: #d6b674;
}

/* ------------------------------------------------------------------ */
/* Gold buttons                                                         */
/* ------------------------------------------------------------------ */
.btn-gold {
  background: #fff !important;
  color: #d6b674 !important;
  border: 1px solid #d6b674 !important;
  border-radius: 6px;
}

.btn-gold:hover,
.btn-gold:focus,
.btn-gold:active {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%) !important;
  color: #fff !important;
  border-color: #d6b674 !important;
}

@media (max-width: 576px) {
  .btn-gold {
    font-size: 0.7rem !important;
    padding: 3px 7px !important;
  }
  .btn-gold .btn-text {
    display: none;
  }
}

.btn-pending {
  border-color: #d6b674 !important;
  color: #c4a55a !important;
  border-radius: 6px;
}

.btn-processing-blocked {
  background: rgba(108,117,125,0.08) !important;
  border: 1px solid rgba(108,117,125,0.35) !important;
  color: #6c757d !important;
  border-radius: 6px;
  cursor: default;
  opacity: 1 !important;
  white-space: nowrap;
}

/* ------------------------------------------------------------------ */
/* Pending request rows                                                 */
/* ------------------------------------------------------------------ */
.request-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: linear-gradient(90deg, rgba(214,182,116,0.08) 0%, rgba(214,182,116,0.02) 100%);
  border: 1px solid rgba(214,182,116,0.4);
  border-radius: 12px;
  margin-bottom: 10px;
}

/* ------------------------------------------------------------------ */
/* Member rows                                                          */
/* ------------------------------------------------------------------ */
.connections-scroll {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.connections-scroll::-webkit-scrollbar {
  width: 5px;
}
.connections-scroll::-webkit-scrollbar-track {
  background: rgba(214,182,116,0.08);
  border-radius: 4px;
}
.connections-scroll::-webkit-scrollbar-thumb {
  background: rgba(214,182,116,0.5);
  border-radius: 4px;
}

.member-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  margin-bottom: 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.member-row:hover {
  border-color: #d6b674;
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.15);
}

.member-row-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.member-row-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
}

.member-row-info {
  flex: 1;
  min-width: 0;
}

.member-row-name {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-row-location {
  font-size: 0.78rem;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-row-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media (max-width: 576px) {
  .member-row {
    gap: 8px;
    padding: 8px 10px;
  }

  .member-row-avatar {
    width: 36px;
    height: 36px;
  }

  .member-row-name {
    font-size: 0.78rem;
  }

  .member-row-location {
    display: none;
  }

  .member-row-actions .btn {
    font-size: 0.7rem;
    padding: 3px 8px;
    white-space: nowrap;
  }
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  position: relative;
}
</style>
